import React, { useCallback, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Step,
  StepLabel,
  Stepper,
  SvgIcon,
  TextField,
  Typography,
} from "@material-ui/core";
import { SvgIconProps } from "@material-ui/core/SvgIcon";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  FileCopyOutlined,
  OpenInNew,
  WarningOutlined,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { Controller, useForm } from "react-hook-form";
import { Issue2faSetupInfoViewModel } from "../../../api/axcloud";
import { useAppDispatch } from "../../../app/store";
import { showError, showSuccess } from "../../alerts/alertsSlice";
import { enableTwoFactor, TwoFactorStatus } from "../securitySlice";
import { StoreUrlQrDialog } from "./StoreUrlQrDialog";

// 各ストアにある認証アプリのURL
export const AUTHENTICATOR_STORE_URLS = {
  microsoftAppStore:
    "https://apps.apple.com/jp/app/microsoft-authenticator/id983156458",
  microsoftGooglePlay:
    "https://play.google.com/store/apps/details?id=com.azure.authenticator",
  googleAppStore:
    "https://apps.apple.com/jp/app/google-authenticator/id388497605",
  googleGooglePlay:
    "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
};

const AUTHENTICATOR_APPS = [
  {
    name: "Microsoft Authenticator（推奨）",
    appStore: AUTHENTICATOR_STORE_URLS.microsoftAppStore,
    googlePlay: AUTHENTICATOR_STORE_URLS.microsoftGooglePlay,
  },
  {
    name: "Google Authenticator",
    appStore: AUTHENTICATOR_STORE_URLS.googleAppStore,
    googlePlay: AUTHENTICATOR_STORE_URLS.googleGooglePlay,
  },
];

function QrCodeIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm9-2h2v2h-2zm4 0h3v2h-3zm-4 4h2v2h-2zm4 0h2v3h-2zm-4 3h2v1h-2zm6-7h1v2h-1z" />
    </SvgIcon>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    setupDialog: {
    },
    setupContent: {
      paddingTop: theme.spacing(0.5),
    },
    stepper: {
      padding: theme.spacing(0, 0, 2),
    },
    setupHeading: {
      margin: theme.spacing(0.5, 0, 1),
    },
    supportedTitle: {
      margin: theme.spacing(2, 0, 1),
    },
    appList: {
      borderTop: `1px solid ${theme.palette.divider}`,
      marginBottom: theme.spacing(2),
    },
    appRow: {
      minHeight: 76,
      display: "grid",
      gridTemplateColumns: "1.2fr 1fr 1fr",
      alignItems: "center",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    appName: {
      paddingLeft: theme.spacing(1),
    },
    appLinks: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(0.7),
    },
    appLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: theme.spacing(0.3),
      color: theme.palette.info.main,
      textDecoration: "none",
      cursor: "pointer",
      background: "none",
      border: 0,
      padding: 0,
    },
    qr: {
      display: "flex",
      justifyContent: "center",
      width: 320,
      maxWidth: "100%",
      margin: theme.spacing(1, 0),
      "& canvas": {
        display: "block",
        width: "100%",
        height: "auto",
      },
    },
    key: {
      width: 420,
      height: 40,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    otpForm: {
      marginTop: theme.spacing(1),
    },
    otpInput: {
      width: 420,
      marginTop: theme.spacing(1),
      "& .MuiOutlinedInput-root": {
        height: 40,
      },
    },
    warning: {
      margin: theme.spacing(1, 0, 1.5),
    },
    recoveryCodes: {
      margin: theme.spacing(1, 0, 2),
      paddingLeft: theme.spacing(2.5),
      fontFamily: "Arial, sans-serif",
    },
    requiredMark: {
      marginLeft: theme.spacing(0.5),
    },
    dialogActions: {
      padding: theme.spacing(1, 2),
    },
  })
);

interface RegisterCodeValues {
  registerCode: string;
}

const formatSharedKey = (sharedKey: string | null | undefined) =>
  sharedKey?.match(/.{1,4}/g)?.join(" ") ?? "";

function AuthenticatorStoreList({
  onShowQr,
}: {
  onShowQr: (url: string) => void;
}) {
  const classes = useStyles();

  return (
    <>
      <Typography className={classes.supportedTitle} variant="subtitle1">
        サポートしている認証アプリ
      </Typography>
      <div className={classes.appList}>
        {AUTHENTICATOR_APPS.map((app) => (
          <div className={classes.appRow} key={app.name}>
            <Typography className={classes.appName} variant="body2">
              {app.name}
            </Typography>
            <div className={classes.appLinks}>
              <Link
                className={classes.appLink}
                href={app.appStore}
                target="_blank"
                rel="noopener"
                variant="body2"
              >
                App Store <OpenInNew fontSize="inherit" />
              </Link>
              <Link
                className={classes.appLink}
                component="button"
                variant="body2"
                type="button"
                onClick={() => onShowQr(app.appStore)}
              >
                QRコード表示 <QrCodeIcon fontSize="inherit" />
              </Link>
            </div>
            <div className={classes.appLinks}>
              <Link
                className={classes.appLink}
                href={app.googlePlay}
                target="_blank"
                rel="noopener"
                variant="body2"
              >
                Google Play <OpenInNew fontSize="inherit" />
              </Link>
              <Link
                className={classes.appLink}
                component="button"
                variant="body2"
                type="button"
                onClick={() => onShowQr(app.googlePlay)}
              >
                QRコード表示 <QrCodeIcon fontSize="inherit" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export interface EnableTwoFactorDialogProps {
  open: boolean;
  accessToken: string;
  setupInfo: Issue2faSetupInfoViewModel | null | undefined;
  status?: TwoFactorStatus;
  onClose: () => void;
}

export const EnableTwoFactorDialog: React.VFC<EnableTwoFactorDialogProps> = ({
  open,
  accessToken,
  setupInfo,
  status,
  onClose,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [storeQrUrl, setStoreQrUrl] = useState<string | null>(null);
  const [stored, setStored] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<RegisterCodeValues>({ mode: "all" });

  useEffect(() => {
    if (!open) {
      return;
    }
    setActiveStep(0);
    setRecoveryCodes(null);
    setStored(false);
    setStoreQrUrl(null);
    reset();
  }, [open, reset]);

  const onRegister = useCallback(
    async (values: RegisterCodeValues) => {
      const result = await dispatch(
        enableTwoFactor({
          accessToken,
          registerCode: values.registerCode,
        })
      );
      if (enableTwoFactor.fulfilled.match(result)) {
        setRecoveryCodes(result.payload.recoveryCodes ?? []);
        setActiveStep(1);
      }
    },
    [accessToken, dispatch]
  );

  const handleClose = useCallback(() => {
    if (status === "enabling" || (activeStep === 1 && !stored)) {
      return;
    }
    setRecoveryCodes(null);
    onClose();
  }, [activeStep, onClose, status, stored]);

  const copyRecoveryCodes = useCallback(async () => {
    const text = (recoveryCodes ?? []).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      dispatch(showSuccess("リカバリーコードをクリップボードにコピーしました"));
    } catch (_error) {
      dispatch(
        showError(
          "リカバリーコードをコピーできませんでした。画面から選択してコピーしてください。"
        )
      );
    }
  }, [dispatch, recoveryCodes]);

  const steps = ["二要素認証の設定手順", "完了"];

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, _reason) => {
          // 完了画面は保管確認まで外側クリック・Escapeで閉じない。
          if (activeStep === 0) {
            handleClose();
          }
        }}
        disableEscapeKeyDown={activeStep === 1 || status === "enabling"}
        scroll="paper"
        fullWidth
        maxWidth="md"
        PaperProps={{ className: classes.setupDialog }}
      >
        <DialogTitle>二要素認証の設定</DialogTitle>
        <DialogContent className={classes.setupContent}>
          <Stepper activeStep={activeStep} className={classes.stepper}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ステップ1: 二要素認証の案内 */}
          {activeStep === 0 && (
            <>
              <Typography className={classes.setupHeading} variant="subtitle1">
                1. 認証アプリをインストール
              </Typography>
              <Typography variant="body1">
                二要素認証の設定には、下記の認証アプリのインストールが必要です。
              </Typography>
              <AuthenticatorStoreList onShowQr={setStoreQrUrl} />

              <Typography className={classes.setupHeading} variant="subtitle1">
                2. QRコードの読み取り
              </Typography>
              <Typography variant="body1">
                アプリを起動し、下記のQRコードを読み取ってください。
                <br />
                二要素認証に必要な6桁のワンタイムパスワードが画面に表示されます。
              </Typography>
              {setupInfo?.authenticatorUri && (
                <div
                  className={classes.qr}
                  role="img"
                  aria-label="Authenticator登録用QRコード"
                >
                  <QRCodeCanvas
                    value={setupInfo.authenticatorUri}
                    size={320}
                    includeMargin
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>
              )}
              <Typography variant="body1">
                QRコードが読み取れない場合は、アプリの設定画面で下記のキーを入力してください。
              </Typography>
              <TextField
                className={classes.key}
                label="キー"
                value={formatSharedKey(setupInfo?.sharedKey)}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />

              <Typography className={classes.setupHeading} variant="subtitle1">
                3. ワンタイムパスワード（6桁）の入力
              </Typography>
              <Typography variant="body1">
                画面に表示された6桁のワンタイムパスワードを入力してください。
              </Typography>
              <form
                className={classes.otpForm}
                id="enableTwoFactorForm"
                onSubmit={handleSubmit(onRegister)}
              >
                <Controller
                  control={control}
                  name="registerCode"
                  rules={{ required: "ワンタイムパスワードを入力してください" }}
                  render={({ field }) => {
                    return <TextField
                      {...field}
                      className={classes.otpInput}
                      label="ワンタイムパスワード"
                      variant="outlined"
                      // WHY: 常にshrinkしないと内部のlegendの変動でフォーカスのたびに高さが変わり、カクつくため
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 6, inputMode: "numeric" }}
                      error={Boolean(errors.registerCode)}
                      helperText={errors.registerCode?.message ?? " "}
                      autoFocus
                    />
                  }}
                />
              </form>
            </>
          )}

          {/* ステップ2: 二要素認証完了 & リカバリーコード表示 */}
          {activeStep === 1 && (
            <>
              <Typography variant="body1">
                設定が完了しました。
              </Typography>
              <Alert
                className={classes.warning}
                severity="warning"
                icon={<WarningOutlined />}
              >
                <Typography variant="body2">
                  リカバリーコードを安全な場所に保管してください
                </Typography>
                <Typography variant="body2">
                  モバイル端末の紛失や故障などによりワンタイムパスワードを表示できなくなった場合には、リカバリーコードを使ってログインすることができます。
                  <br />
                  それぞれのリカバリーコードは1度だけ使用することができます。
                  <br />
                  不測の事態に備え、リカバリーコードをコピーして安全な場所に保管してください。
                </Typography>
              </Alert>
              <Button
                color="primary"
                variant="contained"
                startIcon={<FileCopyOutlined />}
                onClick={copyRecoveryCodes}
              >
                クリップボードにコピー
              </Button>
              <Typography
                className={classes.recoveryCodes}
                component="ul"
                variant="body2"
              >
                {(recoveryCodes ?? []).map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stored}
                    onChange={(event) => setStored(event.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <span>
                    リカバリーコードを保管しました。
                    <span className={classes.requiredMark}>*</span>
                  </span>
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {activeStep === 0 && (
            <>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                form="enableTwoFactorForm"
                disabled={!isValid || status === "enabling" || !setupInfo}
              >
                {status === "enabling" ? <CircularProgress size={22} /> : "設定する"}
              </Button>
              <Button onClick={handleClose} disabled={status === "enabling"}>
                キャンセル
              </Button>
            </>
          )}
          {activeStep === 1 && (
            <Button onClick={handleClose} color="primary" disabled={!stored}>
              閉じる
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <StoreUrlQrDialog
        url={storeQrUrl}
        onClose={() => setStoreQrUrl(null)}
      />
    </>
  );
};
